import { Node, mergeAttributes } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import type { CrossReferenceAttrs } from '@imsenta/shared';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    crossReference: {
      insertCrossReference: (attrs: Partial<CrossReferenceAttrs>) => ReturnType;
    };
    callout: {
      toggleCallout: (kind?: string) => ReturnType;
    };
  }
}

/* ── Cross reference ──────────────────────────────────────────────── */

function CrossReferenceView({ node }: { node: { attrs: Record<string, unknown> } }) {
  const attrs = node.attrs as unknown as CrossReferenceAttrs;
  const broken = !attrs.documentId && !attrs.sectionAnchor && !attrs.url;
  const suffix = attrs.documentRef ? ` · ${attrs.documentRef}` : '';
  return (
    <NodeViewWrapper as="span" className="pf-xref" data-broken={broken ? 'true' : 'false'} title={broken ? 'This reference no longer resolves' : `${attrs.label}${suffix}`}>
      {attrs.label || attrs.documentRef || 'reference'}
    </NodeViewWrapper>
  );
}

/**
 * An inline reference to another document or section.
 *
 * Storing the target id rather than a literal link means a reference survives
 * the target being renamed, and the Markdown exporter can resolve it to a
 * stable path at export time.
 */
export const CrossReference = Node.create({
  name: 'crossReference',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return {
      documentId: { default: null },
      documentRef: { default: null },
      sectionAnchor: { default: null },
      label: { default: 'reference' },
      url: { default: null },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-cross-reference]' }];
  },

  renderHTML({ HTMLAttributes, node }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, { 'data-cross-reference': '', class: 'pf-xref' }),
      String(node.attrs.label ?? 'reference'),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(CrossReferenceView);
  },

  addCommands() {
    return {
      insertCrossReference:
        (attrs) =>
        ({ commands }) =>
          commands.insertContent({
            type: this.name,
            attrs: {
              documentId: attrs.documentId ?? null,
              documentRef: attrs.documentRef ?? null,
              sectionAnchor: attrs.sectionAnchor ?? null,
              label: attrs.label ?? 'reference',
              url: attrs.url ?? null,
            },
          }),
    };
  },
});

/* ── Callout ──────────────────────────────────────────────────────── */

export const CALLOUT_KINDS = [
  { kind: 'note', label: 'Note' },
  { kind: 'important', label: 'Important' },
  { kind: 'warning', label: 'Warning' },
  { kind: 'tip', label: 'Tip' },
] as const;

/** A highlighted aside — renders as a GFM alert in Markdown. */
export const Callout = Node.create({
  name: 'callout',
  group: 'block',
  content: 'block+',
  defining: true,

  addAttributes() {
    return { kind: { default: 'note' } };
  },

  parseHTML() {
    return [{ tag: 'div[data-callout]' }];
  },

  renderHTML({ HTMLAttributes, node }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-callout': String(node.attrs.kind ?? 'note'),
        class: `pf-callout pf-callout-${String(node.attrs.kind ?? 'note')}`,
      }),
      0,
    ];
  },

  addCommands() {
    return {
      toggleCallout:
        (kind = 'note') =>
        ({ commands, editor }) =>
          editor.isActive(this.name) ? commands.lift(this.name) : commands.wrapIn(this.name, { kind }),
    };
  },
});
